<script setup lang="ts">
import type { CcMode } from '../features/cc/ccApi';
import { useCcPolicy } from '../features/cc/useCcPolicy';

// Parent keys the component by domain ID; requests/queued saves belong only to this captured domain.
const props = defineProps<{ domainId: number }>();
const { view, draft, loading, saving, loadError, saveError, publishError, fieldNotes, editable, dirty,
  statusText, durationText, pathInput, pathMatch, pathError, remoteConflict,
  setMode, setEnabled, addPath, removePath, pathFocusOut, reload, retrySave } = useCcPolicy(props.domainId);
const modes: { value: CcMode; label: string; description: string }[] = [
  { value: 'RELAXED', label: '宽松', description: '极少干预，保留高阈值保护' },
  { value: 'STANDARD', label: '标准', description: '默认方案，兼顾正常访问与防护' },
  { value: 'STRICT', label: '严格', description: '更早限制异常高频访问' }
];
</script>

<template>
  <section class="cc-panel" aria-labelledby="cc-title" data-test="cc-panel">
    <header class="cc-heading">
      <div><p class="cc-eyebrow">独立流量防护</p><h2 id="cc-title">CC 防御</h2><p>修改后 1 秒自动保存。后台发布不影响继续操作，也不刷新其他区域。</p></div>
      <button type="button" class="cc-secondary" :disabled="saving" @click="reload">读取最新配置</button>
    </header>
    <p v-if="loading" role="status">正在读取此域名的 CC 配置...</p>
    <p v-else-if="loadError" class="cc-error" role="alert">{{ loadError }}</p>
    <template v-else>
      <p v-if="view?.policy?.mode === 'OBSERVE'" class="cc-warning">当前为管理员设置的观察模式；此模式只能由管理员调整。</p>
      <label class="cc-enabled">
        <input type="checkbox" :checked="draft.enabled" :disabled="!editable" data-test="cc-enabled"
          @change="setEnabled(($event.target as HTMLInputElement).checked)">
        <strong>启用 CC 防御</strong><span>不改变已有的 WAF 与访问控制设置</span>
      </label>
      <p v-if="fieldNotes.enabled" class="cc-note" data-test="cc-note-enabled" aria-live="polite">{{ fieldNotes.enabled }}</p>
      <fieldset class="cc-modes" :disabled="!editable">
        <legend>防御模式 <span class="cc-required">*</span></legend>
        <label v-for="mode in modes" :key="mode.value" class="cc-mode" :class="{ selected: draft.mode === mode.value }">
          <input type="radio" name="cc-mode" :value="mode.value" :checked="draft.mode === mode.value"
            :data-test="`cc-mode-${mode.value}`" @change="setMode(mode.value)">
          <span><strong>{{ mode.label }}</strong><small>{{ mode.description }}</small></span>
          <span v-if="mode.value === 'STANDARD'" class="cc-recommended">默认</span>
        </label>
      </fieldset>
      <p v-if="fieldNotes.mode" class="cc-note" data-test="cc-note-mode" aria-live="polite">{{ fieldNotes.mode }}</p>
      <div class="cc-rules">
        <strong>规则顺序</strong>
        <p>任何黑名单优先拦截。IP 和 IP 集合白名单免 CC 及回源限流；国家/地区白名单仍受 CC 限流。</p>
      </div>
      <section class="cc-paths" aria-labelledby="cc-paths-title">
        <h3 id="cc-paths-title">路径豁免 <span>选填</span></h3>
        <p>只豁免 CC，不跳过访问控制、WAF 或回源保护。支持精确匹配与目录前缀，最多 128 条。</p>
        <ul v-if="draft.pathExceptions.length" class="cc-path-list">
          <li v-for="item in draft.pathExceptions" :key="item.id">
            <span class="cc-path-kind">{{ item.matchType === 'EXACT' ? '精确' : '前缀' }}</span><code>{{ item.path }}</code>
            <button type="button" :disabled="!editable" :aria-label="`删除路径 ${item.path}`" @click="removePath(item.id)">删除</button>
          </li>
        </ul>
        <div class="cc-path-editor" data-test="cc-path-editor" @focusout="pathFocusOut">
          <select v-model="pathMatch" aria-label="路径匹配方式" :disabled="!editable">
            <option value="EXACT">精确匹配</option><option value="PREFIX">目录前缀</option>
          </select>
          <input v-model="pathInput" type="text" aria-label="新增豁免路径" :aria-invalid="!!pathError" aria-describedby="cc-path-help cc-path-error"
            placeholder="例如 /healthz，粘贴后可以继续修改" maxlength="1024" :disabled="!editable"
            data-test="cc-path-input" @keydown.enter.prevent="addPath">
          <button type="button" :disabled="!editable || !pathInput.trim()" @click="addPath">添加路径</button>
        </div>
        <p id="cc-path-help" class="cc-help">回车、点击添加或点击输入区域外确认；空白失焦不会删除已有路径。</p>
        <p v-if="pathError" id="cc-path-error" class="cc-error" role="alert">{{ pathError }}</p>
        <p v-if="fieldNotes.paths" class="cc-note" data-test="cc-note-paths" aria-live="polite">{{ fieldNotes.paths }}</p>
      </section>
      <footer class="cc-publication" :class="{ applied: view?.publishState === 'APPLIED' && !dirty && !saving }">
        <div aria-live="polite">
          <strong data-test="cc-publish-state">{{ statusText }}</strong>
          <p v-if="view?.configured">已保存版本 v{{ view.policyVersion }}<span v-if="view.policy"> · 模板 v{{ view.policy.templateVersion }}</span></p>
          <p v-if="!view?.dispatchEnabled" class="cc-warning">节点自动发布尚未启用；保存配置不代表已经在线生效。</p>
          <p v-else-if="view.publication?.message">{{ view.publication.message }}</p>
          <p v-if="durationText">{{ durationText }}</p>
          <p v-if="publishError" class="cc-error" role="alert">{{ publishError }}</p>
          <p v-if="saveError" class="cc-error" role="alert">{{ saveError }}</p>
        </div>
        <button type="button" class="cc-primary" :disabled="!editable || !dirty || remoteConflict" data-test="cc-save" @click="retrySave">
          {{ saving ? '保存中，可继续修改' : saveError ? '重试保存' : '立即保存' }}
        </button>
      </footer>
    </template>
  </section>
</template>

<style scoped>
.cc-panel { --cc-green: #15583d; --cc-muted: #60766b; border: 1px solid #d7e3d9; border-radius: 16px; padding: 24px; background: linear-gradient(125deg, #fffef9, #f8fcf8); color: #163528; }
.cc-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 18px; margin-bottom: 24px; }
.cc-heading h2 { margin: 4px 0 8px; font-size: 24px; }
.cc-heading p, .cc-paths > p, .cc-rules p, .cc-publication p { margin: 6px 0; color: var(--cc-muted); font-size: 13px; line-height: 1.65; }
.cc-heading .cc-eyebrow { font-size: 11px; letter-spacing: .12em; color: var(--cc-green); }
.cc-enabled { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.cc-enabled span { color: var(--cc-muted); font-size: 12px; }
input[type='checkbox'], input[type='radio'] { accent-color: var(--cc-green); width: 17px; height: 17px; flex-shrink: 0; }
.cc-modes { border: 0; padding: 0; margin: 24px 0 12px; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.cc-modes legend { font-size: 14px; font-weight: 600; margin-bottom: 12px; }
.cc-required { color: #bc3e37; }
.cc-mode { display: flex; gap: 10px; align-items: flex-start; position: relative; padding: 18px 12px; border: 1px solid #dbe4dd; border-radius: 10px; background: #fff; cursor: pointer; }
.cc-mode.selected { border-color: var(--cc-green); background: #eef6ec; box-shadow: inset 0 0 0 1px var(--cc-green); }
.cc-mode strong { display: block; font-size: 16px; margin-bottom: 7px; }
.cc-mode small { color: var(--cc-muted); font-size: 12px; line-height: 1.6; display: block; }
.cc-recommended { margin-left: auto; color: var(--cc-green); font-size: 10px; white-space: nowrap; }
.cc-note { font-size: 12px; color: var(--cc-green); margin: 8px 0; }
.cc-rules { margin-top: 20px; border-left: 3px solid #a2bf95; padding: 3px 14px; }
.cc-rules strong { font-size: 12px; }
.cc-paths { margin-top: 26px; }
.cc-paths h3 { font-size: 15px; margin: 0 0 8px; }
.cc-paths h3 span { font-size: 11px; color: var(--cc-muted); font-weight: 400; margin-left: 6px; }
.cc-path-list { margin: 14px 0; padding: 0; list-style: none; display: grid; gap: 6px; }
.cc-path-list li { display: flex; align-items: center; gap: 10px; border: 1px solid #dce6de; border-radius: 7px; padding: 8px 10px; background: #fff; min-width: 0; }
.cc-path-kind { color: var(--cc-muted); font-size: 11px; flex-shrink: 0; }
.cc-path-list code { overflow-wrap: anywhere; font-size: 13px; }
.cc-path-list button { margin-left: auto; background: transparent; color: #ad423c; font-size: 12px; flex-shrink: 0; }
.cc-path-editor { display: flex; gap: 8px; margin-top: 14px; }
.cc-path-editor input { min-width: 0; flex: 1; }
.cc-path-editor input, .cc-path-editor select { min-height: 38px; border: 1px solid #cbdad0; border-radius: 7px; padding: 8px 10px; background: #fff; color: #183e2b; font: inherit; font-size: 13px; }
.cc-path-editor input[aria-invalid='true'] { border-color: #bd4339; }
.cc-panel button { border: 1px solid #cadace; border-radius: 7px; padding: 9px 12px; font: inherit; font-size: 12px; cursor: pointer; background: #f5faf4; color: var(--cc-green); }
.cc-panel button:disabled, .cc-panel fieldset:disabled { opacity: .6; cursor: default; }
.cc-panel :is(button, input, select):focus-visible { outline: 2px solid #23764e; outline-offset: 3px; }
.cc-panel .cc-primary { color: #fff; background: var(--cc-green); border-color: var(--cc-green); white-space: nowrap; }
.cc-panel .cc-secondary { background: #fff; white-space: nowrap; }
.cc-panel .cc-error { color: #ad352e; font-size: 13px; line-height: 1.65; }
.cc-panel .cc-warning { color: #876216; }
.cc-publication { border: 1px solid #e2e6de; background: #f6f7f1; border-radius: 10px; padding: 14px 16px; margin-top: 24px; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.cc-publication strong { font-size: 14px; }
.cc-publication.applied { background: #edf7ed; border-color: #b7d4b9; }
@media (max-width: 780px) { .cc-panel { padding: 18px; } .cc-modes { grid-template-columns: 1fr; } .cc-heading, .cc-publication { flex-direction: column; } .cc-path-editor { flex-wrap: wrap; } .cc-path-editor input { flex-basis: calc(100% - 135px); } }
@media (prefers-reduced-motion: reduce) { .cc-panel * { transition: none !important; } }
</style>
